import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, MapLocKeys } from '@cm/core/localization';

const dpRoot = 'Dashboard Report|Certification Verification';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	feature: HubFeature.ComptiaCustomDashboardReports,
	htmlAllowed: false,
	portal: 'SP',
};

const htmlContent = `
<p>
    Press the Verify Candidates button, then select a CSV file filled with candidate information to receive a CSV file with certification verification for each matched candidate.
</p>
<p><strong>To create the Comma Separated File (CSV) input verification file: </strong></p>
<p>
    The following column headers are recognized by the parser:
</p>
<ul>
	<li>Candidate ID Or Voucher ID (required)</li>
	<li>First Name (required)</li>
	<li>Last Name (required)</li>
	<li>Middle Initial (optional) or Middle Name (optional) may use one or the other, can not use both.</li>
	<li>Middle Name (optional)</li>
</ul>
<p>
    The first name field is a combination of the candidate's first and middle initial/name. For instance, candidate "Jane Q. Doe" is 
    represented in the CSV input file with a first name of "Jane Q." and a last name of "Doe". If your system has the first name and 
    middle name fields separate, please combine the fields into the "First Name" column for the CVS input file.
</p>
<p>
    Optionally, a "Middle Initial" or "Middle Name" column can be added to the CSV input file. When the CSV input file is submitted to 
    this web page, the "FirstName" and "Middle Initial"/"Middle Name" columns will be concatenated before matching occurs. For example, 
    a candidate with the first name "John" and a middle name/initial of "L" will become the first name of "John L", then compared to the 
    first name field for this candidate. An error will be generated if both the "Middle Initial" and "Middle Name" fields are provided in 
    the same CVS input file.
</p>
<p>
    Below the column header line, add a line for each candidate seeking verification. Note: the Career ID, First Name, Last Name, and optional 
    Middle Initial/Middle Name data given must match exactly to a candidate in the system before certification verification data will be returned.
</p>
<p><strong>Return CSV file: </strong></p>
<p>
    After pressing the Verify Candidates button, a CSV file will be returned with following columns:
</p>
<ul>
	<li>Candidate ID/Voucher ID</li>
	<li>First Name</li>
	<li>Last Name</li>
	<li>EDIPN</li>
	<li>Status</li>
	<li>Cert Name</li>
	<li>Cert Classification (optional)</li>
	<li>Cert Group</li>
	<li>Cert Active Date</li>
	<li>Cert Inactive Date</li>
</ul>
<p>
    If the "Middle Initial" or "Middle Name" columns were provided in the input CSV file, the same column will be echoed back in the CSV output file.
</p>
<p>
    The Status column will contain one of the following values, defined as:
</p>
<ul>
	<li>No Match ID - the Candidate ID/Voucher ID did not match.</li>
	<li>No Permission - the Candidate ID/Voucher ID matched, but the candidate does not allow verification data to be given.</li>
	<li>No Match Name - the Candidate ID/Voucher ID matched, but one of the names did not match.</li>
	<li>No Certs - The Candidate ID/Voucher ID and names matched, but this candidate does not have any certifications.</li>
	<li>Inactive - All fields matched, but the given certification is inactive or has expired.</li>
	<li>Active - All fields matched and the given certification is currently active.</li>
</ul>
<p>
    If a certification is active or inactive, the certification name, group, activation date, expiration date (if any), 
    and (optional) classification will be given.
</p>
<p>
    Returned CSV data is sorted by Candidate ID, then certification activation date.
</p>
<p>
    If the output file is small (1 KB or less) or is empty, the file might contain different end-of-line characters. For example, a Linux 
    generated CSV file (the default) will contain only a newline character (i.e. a "\n", 0x0A in a binary editor) at the end of each line. 
    Windows generated CSV files, including those generated by Excel, use a carriage return/line feed combination (i.e. a "\r\n" or "\r", a 
    0x0D0A or 0x0D in a binary editor). Please check the box entitled "Use Windows style end of line characters" and try the file again.
</p>
`;

const singleHtmlContent = `
<p>
	To look up a single certification verification:
</p>
<ul>
	<li>Select either the Candidate ID or Voucher ID</li>
	<li>Enter the Candidate's First name (middle name/initial) Last name</li>
	<li>Click "Verify candidate"</li>
</ul>
`;

export type SPCertVerificationLocNames =
	| 'SPCertVerificationPageTitle'
	| 'SPSingleCertVerificationPageTitle'
	| 'SPCertVerificationPageHeading'
	| 'SPSingleCertVerificationButton'
	| 'SPCertVerificationButton'
	| 'SPCertVerificationInstructions'
	| 'SPSingleCertVerificationInstructions'
	| 'SPCertVerificationInvalidRowsError'
	| 'SPCertVerificationInvalidMiddleNameError'
	| 'SPCertVerificationInvalidIdError'
	| 'SPSingleCertVerificationFirstNameLabel'
	| 'SPSingleCertVerificationLastNameLabel'
	| 'SPSingleCertVerificationIdTypeLabel'
	| 'SPSingleCertVerificationIdLabel'
	| 'SPSingleCertVerificationIdValueLabel'
	| 'SPSingleCertVerificationIdTypeCandidateId'
	| 'SPSingleCertVerificationIdTypeVoucherId'
	| 'SPSingleCertVerificationFirstNameRequiredError'
	| 'SPSingleCertVerificationLastNameRequiredError'
	| 'SPSingleCertVerificationIdRequiredError'
	| 'SPSingleCertVerificationGroupColumnLabel'
	| 'SPSingleCertVerificationCertificationColumnLabel'
	| 'SPSingleCertVerificationClassificationColumnLabel'
	| 'SPSingleCertVerificationStatusColumnLabel'
	| 'SPSingleCertVerificationActiveDateColumnLabel'
	| 'SPSingleCertVerificationInactiveDateColumnLabel'
	| 'SPSingleCertVerificationCriteriaColumnLabel';

export const spCertVerificationLocs: LocDefs<SPCertVerificationLocNames> = {
	SPCertVerificationPageTitle: {
		defaultValue: 'Certification Verification',
		description: 'Page title for the cert verification page section',
		displayPath: `${dpRoot}|Page Title`,
		displayType: 'Title',
		...sharedProperties,
	},
	SPSingleCertVerificationPageTitle: {
		defaultValue: 'Single Certification Verification',
		description: 'Page title for the single cert verification page section',
		displayPath: `${dpRoot}|Single Page Title`,
		displayType: 'Title',
		...sharedProperties,
	},
	SPCertVerificationPageHeading: {
		defaultValue: 'Certification Verification',
		description: 'Page Heading for the cert verification page',
		displayPath: `${dpRoot}|Page Heading`,
		displayType: 'Title',
		...sharedProperties,
	},
	SPSingleCertVerificationButton: {
		defaultValue: 'Verify Candidate',
		description: 'Button to verify a single candidate for the cert verification page',
		displayPath: `${dpRoot}|Single Verify Candidates Button`,
		displayType: 'Button',
		...sharedProperties,
	},
	SPCertVerificationButton: {
		defaultValue: 'Verify Candidates',
		description: 'Button to verify candidates for the cert verification page',
		displayPath: `${dpRoot}|Verify Candidates Button`,
		displayType: 'Button',
		...sharedProperties,
	},
	SPCertVerificationInstructions: {
		defaultValue: htmlContent,
		description: 'Instructions for the cert verification section',
		displayPath: `${dpRoot}|Instructions`,
		displayType: 'Content',
		shared: false,
		feature: HubFeature.ComptiaCustomDashboardReports,
		htmlAllowed: true,
		portal: 'SP',
	},
	SPSingleCertVerificationInstructions: {
		defaultValue: singleHtmlContent,
		description: 'Instructions for the single cert verification section',
		displayPath: `${dpRoot}|Single Instructions`,
		displayType: 'Content',
		shared: false,
		feature: HubFeature.ComptiaCustomDashboardReports,
		htmlAllowed: true,
		portal: 'SP',
	},
	SPCertVerificationInvalidRowsError: {
		defaultValue: 'Invalid rows in the CSV file',
		description: 'Error message for invalid rows in the CSV file',
		displayPath: `${dpRoot}|Invalid Rows Error`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPCertVerificationInvalidMiddleNameError: {
		defaultValue: 'Each row can have either Middle Initial or Middle Name, not both.',
		description: 'Error message for invalid middle name in the CSV file',
		displayPath: `${dpRoot}|Invalid Middle Name Error`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPCertVerificationInvalidIdError: {
		defaultValue: 'Each row must have either a Candidate ID or Voucher ID',
		description: 'Error message for invalid ID in the CSV file',
		displayPath: `${dpRoot}|Invalid ID Error`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPSingleCertVerificationFirstNameLabel: {
		defaultValue: 'First Name (middle name/initial)',
		description: 'Label for the first name field in the single cert verification section',
		displayPath: `${dpRoot}|Single First Name Label`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPSingleCertVerificationLastNameLabel: {
		defaultValue: 'Last Name',
		description: 'Label for the last name field in the single cert verification section',
		displayPath: `${dpRoot}|Single Last Name Label`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPSingleCertVerificationIdTypeLabel: {
		defaultValue: 'ID Type',
		description: 'Label for the ID type field in the single cert verification section',
		displayPath: `${dpRoot}|Single ID Type Label`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPSingleCertVerificationIdLabel: {
		defaultValue: 'ID',
		description: 'Label for the ID field in the single cert verification section',
		displayPath: `${dpRoot}|Single ID Label`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPSingleCertVerificationIdValueLabel: {
		defaultValue: 'ID Value',
		description: 'Label for the ID value field in the single cert verification section',
		displayPath: `${dpRoot}|Single ID Value Label`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPSingleCertVerificationIdTypeCandidateId: {
		defaultValue: 'Candidate ID',
		description: 'Label for the candidate ID option in the single cert verification section',
		displayPath: `${dpRoot}|Single ID Type Candidate ID`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPSingleCertVerificationIdTypeVoucherId: {
		defaultValue: 'Voucher ID',
		description: 'Label for the voucher ID option in the single cert verification section',
		displayPath: `${dpRoot}|Single ID Type Voucher ID`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPSingleCertVerificationFirstNameRequiredError: {
		defaultValue: 'First name is required',
		description: 'Error message for first name in the single cert verification section',
		displayPath: `${dpRoot}|Single First Name Required Error`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPSingleCertVerificationLastNameRequiredError: {
		defaultValue: 'Last name is required',
		description: 'Error message for last name in the single cert verification section',
		displayPath: `${dpRoot}|Single Last Name Required Error`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPSingleCertVerificationIdRequiredError: {
		defaultValue: 'ID is required',
		description: 'Error message for ID in the single cert verification section',
		displayPath: `${dpRoot}|Single ID Required Error`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPSingleCertVerificationGroupColumnLabel: {
		defaultValue: 'Group',
		description: 'Label for the group column in the single cert verification section',
		displayPath: `${dpRoot}|Single Cert Group Column Label`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPSingleCertVerificationCertificationColumnLabel: {
		defaultValue: 'Certification',
		description: 'Label for the certification column in the single cert verification section',
		displayPath: `${dpRoot}|Single Cert Certification Column Label`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPSingleCertVerificationClassificationColumnLabel: {
		defaultValue: 'Classification',
		description: 'Label for the classification column in the single cert verification section',
		displayPath: `${dpRoot}|Single Cert Classification Column Label`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPSingleCertVerificationStatusColumnLabel: {
		defaultValue: 'Status',
		description: 'Label for the status column in the single cert verification section',
		displayPath: `${dpRoot}|Single Cert Status Column Label`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPSingleCertVerificationActiveDateColumnLabel: {
		defaultValue: 'Active Date',
		description: 'Label for the active date column in the single cert verification section',
		displayPath: `${dpRoot}|Single Cert Active Date Column Label`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPSingleCertVerificationInactiveDateColumnLabel: {
		defaultValue: 'Inactive Date',
		description: 'Label for the inactive date column in the single cert verification section',
		displayPath: `${dpRoot}|Single Cert Inactive Date Column Label`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPSingleCertVerificationCriteriaColumnLabel: {
		defaultValue: 'Criteria',
		description: 'Label for the criteria column in the single cert verification section',
		displayPath: `${dpRoot}|Single Cert Criteria Column Label`,
		displayType: 'Label',
		...sharedProperties,
	},
};

export type SPCertVerificationLocType = LocMap<SPCertVerificationLocNames>;

export const CERT_VERIFICATION_LOC_KEYS =
	MapLocKeys<SPCertVerificationLocNames>(spCertVerificationLocs);
